import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress, Link,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Language as LanguageIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  TrendingUp as TrendingUpIcon,
  BoltOutlined as BoltIcon,
  InsightsOutlined as InsightsIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { companyService } from '../../services/companyService';

const validationSchema = Yup.object({
  companyName: Yup.string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters')
    .required('Company name is required'),
  domain: Yup.string()
    .url('Please enter a valid URL')
    .required('Company domain is required'),
  fullName: Yup.string()
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name must be less than 100 characters')
    .required('Full name is required'),
  email: Yup.string()
    .email('Please enter a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain uppercase, lowercase, and a number'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password')
});

const features = [
  { icon: <InsightsIcon />, title: 'Unified analytics', desc: 'Every metric from every platform, one pane of glass.' },
  { icon: <BoltIcon />, title: 'AI agents on demand', desc: 'Purpose-built specialists that answer anything, instantly.' },
  { icon: <TrendingUpIcon />, title: 'Real-time pipelines', desc: 'Fresh data, automated reports, zero manual work.' },
];

const Register = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError('');
      setSuccess('');
      const response = await companyService.registerCompany({
        companyName: values.companyName,
        domain: values.domain,
        fullName: values.fullName,
        email: values.email,
        password: values.password
      });
      if (response?.pending) {
        setPendingApproval(true);
        setSuccess('Registration submitted! We will review your application and email you at ' + values.email + ' once your account has been approved.');
      } else {
        setSuccess('Company registered successfully! Redirecting to dashboard...');
        setTimeout(() => { navigate('/dashboard'); }, 2000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      {/* Left marketing panel — desktop only */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--gradient-marketing)',
          color: '#fff',
        }}
      >
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none',
          background: `
            radial-gradient(circle at 15% 20%, rgba(255,255,255,0.18) 0%, transparent 40%),
            radial-gradient(circle at 85% 75%, rgba(20,184,166,0.3) 0%, transparent 45%),
            radial-gradient(circle at 50% 110%, rgba(139,92,246,0.4) 0%, transparent 50%)
          `,
        }} />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(255,255,255,0.25)',
          }}>
            <BoltIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 800,
            fontSize: '1.05rem',
            letterSpacing: '-0.02em',
          }}>
            SUPERCHARGEME
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', maxWidth: 480 }}>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontSize: { md: '2.5rem', lg: '3rem' },
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            mb: 2,
          }}>
            Join the platform<br/>powering modern brands.
          </Typography>
          <Typography sx={{
            fontSize: '1.05rem',
            opacity: 0.85,
            mb: 5,
            lineHeight: 1.55,
            fontWeight: 400,
          }}>
            Set up your workspace in minutes. Connect your data sources, invite your team,
            and let AI agents do the heavy lifting.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {features.map((f) => (
              <Box key={f.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 1.5,
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  '& svg': { color: '#fff', fontSize: 20 },
                }}>
                  {f.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.25 }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', opacity: 0.8, lineHeight: 1.5 }}>{f.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{ position: 'relative', fontSize: '0.8rem', opacity: 0.65 }}>
          © {new Date().getFullYear()} SuperChargeMe. All rights reserved.
        </Typography>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6, md: 8 },
          py: 6,
          bgcolor: 'background.default',
          overflowY: 'auto',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 460 }}>
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center', gap: 1.25, mb: 4,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              background: 'var(--gradient-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BoltIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontWeight: 800,
              fontSize: '1rem',
              letterSpacing: '-0.02em',
            }}>
              SUPERCHARGEME
            </Typography>
          </Box>

          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 700,
            fontSize: '1.75rem',
            letterSpacing: '-0.025em',
            mb: 1,
          }}>
            Create your workspace
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
            Register your company to get started.
          </Typography>

          {pendingApproval ? (
            <Box>
              {success && <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}
              <Typography sx={{ mb: 3, color: 'text.secondary', fontSize: '0.95rem' }}>
                Our team typically reviews registrations within 24 hours.
              </Typography>
              <Button component={RouterLink} to="/login" variant="outlined" fullWidth sx={{ py: 1.3, borderRadius: 2 }}>
                Back to sign in
              </Button>
            </Box>
          ) : (
            <Formik
              initialValues={{
                companyName: '', domain: '', fullName: '',
                email: '', password: '', confirmPassword: ''
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, errors, touched, getFieldProps }) => (
                <Form noValidate>
                  {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}
                  {success && <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>{success}</Alert>}

                  <TextField
                    {...getFieldProps('companyName')}
                    label="Company name"
                    fullWidth required
                    error={touched.companyName && !!errors.companyName}
                    helperText={touched.companyName && errors.companyName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    {...getFieldProps('domain')}
                    label="Company domain"
                    placeholder="https://yourcompany.com"
                    fullWidth required
                    error={touched.domain && !!errors.domain}
                    helperText={touched.domain && errors.domain}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LanguageIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    {...getFieldProps('fullName')}
                    label="Your full name"
                    fullWidth required
                    error={touched.fullName && !!errors.fullName}
                    helperText={touched.fullName && errors.fullName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    {...getFieldProps('email')}
                    label="Email"
                    type="email"
                    fullWidth required
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    {...getFieldProps('password')}
                    label="Password"
                    type="password"
                    fullWidth required
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    {...getFieldProps('confirmPassword')}
                    label="Confirm password"
                    type="password"
                    fullWidth required
                    error={touched.confirmPassword && !!errors.confirmPassword}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      py: 1.5,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      borderRadius: 2,
                      position: 'relative',
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress
                        size={22}
                        sx={{
                          color: 'white',
                          position: 'absolute',
                          top: '50%', left: '50%',
                          marginTop: '-11px', marginLeft: '-11px',
                        }}
                      />
                    ) : 'Create account'}
                  </Button>

                  <Typography sx={{
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    mt: 4,
                  }}>
                    Already have an account?{' '}
                    <Link
                      component={RouterLink}
                      to="/login"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Sign in
                    </Link>
                  </Typography>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
